import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { SEO, buildLegalServiceSchema, buildAttorneySchema, buildReviewsSchema } from "@/components/seo/SEO";
import { Hero } from "@/components/home/Hero";
import { TrustBadgeStrip } from "@/components/home/TrustBadgeStrip";
import { PracticeAreasGrid } from "@/components/home/PracticeAreasGrid";
import { AttorneyIntro } from "@/components/home/AttorneyIntro";
import { StatsCounter } from "@/components/home/StatsCounter";
import { TestimonialsCarousel } from "@/components/home/TestimonialsCarousel";
import { InsightsPreview } from "@/components/home/InsightsPreview";
import { MotionReveal } from "@/components/shared/MotionReveal";
import { CTAButton } from "@/components/shared/CTAButton";
import { CaseEvaluationDialog } from "@/components/home/CaseEvaluationDialog";
import { FIRM } from "@/constants/firmInfo";
import { apiClient } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    apiClient.get("/testimonials").then((res) => setTestimonials(res.data)).catch(() => {});
  }, []);

  const jsonLd = [buildLegalServiceSchema(), buildAttorneySchema()];
  const reviewsSchema = buildReviewsSchema(testimonials);
  if (reviewsSchema) jsonLd.push(reviewsSchema);

  return (
    <>
      <SEO
        title={t("seo.home.title")}
        description={t("seo.home.description")}
        path="/"
        jsonLd={jsonLd}
      />
      <Hero />
      <TrustBadgeStrip />
      <PracticeAreasGrid />
      <AttorneyIntro />
      <StatsCounter />
      <TestimonialsCarousel />
      <InsightsPreview />

      <section className="relative py-20 sm:py-24 bg-[color:var(--navy-950)] hero-noise overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(900px 500px at 50% 0%, rgba(184,154,61,0.14), transparent 60%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MotionReveal>
            <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)]">{t("finalCta.eyebrow")}</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium text-white">
              {t("finalCta.heading1")} <span className="italic text-[color:var(--gold-400)]">{t("finalCta.headingEmphasis")}</span>
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">
              {t("finalCta.subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <CaseEvaluationDialog
                trigger={<CTAButton variant="brass">{t("finalCta.primaryCta")}</CTAButton>}
              />
              <a href={`tel:${FIRM.phoneTel}`}>
                <CTAButton variant="outline" icon={Phone}>{FIRM.phoneDisplay}</CTAButton>
              </a>
            </div>
          </MotionReveal>
        </div>
      </section>
    </>
  );
}
